/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import systemProductService from 'service/systemProduct';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';
import legalEntitySettingsService from 'service/legalEntitySettings';

interface DiscountHistoryTableProps {
  selectedProductId: string | null;
}

export type discountTypes = {
  name: string;
  barcode: string;
  new_price: string;
  old_price: string;
  created_at: string;
  discount_type: string;
};

const DiscountHistoryTable = ({
  selectedProductId
}: DiscountHistoryTableProps) => {
  const { t } = useTranslation();
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  const { data, isFetching, isLoading } = useQuery(
    ['GET_DISCOUNT_HISTORY', currentPage],
    () => {
      if (selectedProductId) {
        const res = systemProductService
          .getListDiscounthistory({
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            product_id: selectedProductId
          })
          .then((res: any) => {
            setPageCount(Math.ceil(res?.count / 10));

            return res?.items;
          });
        return res;
      }
    },
    { enabled: !!selectedProductId || !!currentPage }
  );

  const loadingData = isLoading || isFetching;

  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const generateData: discountTypes[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

        timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();
        return {
          name: items?.changed_by_user_name,
          discount_type: items?.discount_type,
          discount_amount: items?.discount_amount || 0,
          old_price: items?.old_price || 0,
          new_price: items?.new_price || 0,
          created_date: formatDateToTimeZone(+items?.changed_at, timeZone)
        };
      }) ?? []
    );
  }, [data]);

  const discountHistoryTableColumns: ColumnDef<discountTypes>[] = [
    {
      accessorKey: 'name',
      header: t('users'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'discount_type',
      header: t('discount_type'),
      cell: rowData => {
        const { discount_type } = rowData.row.original;
        return (
          <p className="mb-0">
            {discount_type === 'AMOUNT' ? t('total') : t('percentage')}
          </p>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'discount_amount',
      header: t('discount_pratcentage_or_amount'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'old_price',
      header: t('old_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'new_price',
      header: t('new_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'created_date',
      header: t('created_date'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateData,
    columns: discountHistoryTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loadingData}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </AdvanceTableProvider>
    </>
  );
};

export default DiscountHistoryTable;
